import { useState } from "react";

import { Icon, IconName } from "@blueprintjs/core";
import {
  Column,
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { pluralize } from "@/components/helpers/string-utils";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { OneOneSvg } from "@/components/pages_logged_in/program/components/OneOneSvg";
import {
  Participant,
  renderStatusCell,
  renderUserCell,
} from "@/components/pages_logged_in/program/components/program-participants/participant-table-utils";
import { StatusBadge } from "@/components/pages_logged_in/program/components/StatusBadge";
import {
  isCloseEarlyComponent,
  ONE_ONE_COACHING_STAGE_STYLES,
  roundToOneDecimalPlace,
} from "@/components/pages_logged_in/program/components/utils";
import { Avatar } from "@/components/pieces/users/Avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DARK_GREEN, SUBTEXT_COLOR } from "@/css/constants";
import {
  ComponentTypeEnum,
  GetProgramCoachingSessionsQuery,
  MeetingStatus,
} from "@/graphql";
import { MainComponent } from "@/types/programs";
import { formatDate } from "@/utils/datetime";
import { NonNullableArrayItem } from "@/utils/types";

const useResponseStatusColumns = ({
  component,
  isCloseEarly,
}: {
  component?: Component;
  isCloseEarly: boolean;
}) => {
  const stageColumn = {
    id: "stage",
    header: ({ column }: { column: Column<Participant, unknown> }) => (
      <DataTableColumnHeader column={column} title="Stage" />
    ),
    accessorKey: "stage",
    enableSorting: true,
    sortingFn: (rowA: Row<Participant>, rowB: Row<Participant>) => {
      const stageA = rowA.original.stage || "NotStarted";
      const stageB = rowB.original.stage || "NotStarted";
      return stageA.localeCompare(stageB);
    },
    cell: ({ row }: { row: Row<Participant> }) => {
      const stage = row.original.stage || "NotStarted";
      const stageStyle = ONE_ONE_COACHING_STAGE_STYLES[stage];
      const color = isCloseEarly
        ? SUBTEXT_COLOR
        : stageStyle?.textColor || stageStyle?.color;

      let icon = stageStyle?.icon;

      if (isCloseEarly) {
        icon = (
          <Icon
            icon={stageStyle?.iconClass as IconName}
            size={14}
            color={SUBTEXT_COLOR}
          />
        );
      }
      return (
        <Flex alignItems="center">
          {icon}
          <Box fontSize={14} fontWeight={500} ml="5px" color={color}>
            {stageStyle?.text || "Not Started"}
          </Box>
        </Flex>
      );
    },
  };

  const coachesColumn = {
    id: "coaches",
    header: ({ column }: { column: Column<Participant, unknown> }) => (
      <DataTableColumnHeader column={column} title="Coaches" />
    ),
    accessorKey: "coaches",
    enableSorting: true,
    sortingFn: (rowA: Row<Participant>, rowB: Row<Participant>) => {
      const coachesA = rowA.original.meetings?.map((meeting) => meeting?.coach);
      const coachesB = rowB.original.meetings?.map((meeting) => meeting?.coach);
      const firstCoachA = coachesA?.[0]?.name || "";
      const firstCoachB = coachesB?.[0]?.name || "";
      return firstCoachA.localeCompare(firstCoachB);
    },
    cell: ({ row }: { row: Row<Participant> }) => {
      const coaches = row.original.meetings?.map((meeting) => meeting?.coach);
      const uniqueCoaches: typeof coaches =
        coaches?.filter(
          (coach, index, arr) =>
            coach && arr.findIndex((c) => c?.id === coach.id) === index,
        ) || [];

      return (
        <Flex flexDirection="column" style={{ gap: 12 }}>
          {uniqueCoaches?.map((coach) => (
            <Flex alignItems="center" key={coach?.id}>
              <Box mr={2}>
                <Tooltip>
                  <TooltipTrigger>
                    <Avatar size={20} user={coach} />
                  </TooltipTrigger>
                  <TooltipContent>{coach?.name}</TooltipContent>
                </Tooltip>
              </Box>
              <Box
                ml={1}
                css={{
                  whiteSpace: "nowrap",
                }}
              >
                {coach?.name}
              </Box>
            </Flex>
          ))}
          {!coaches?.length && "-"}
        </Flex>
      );
    },
  };

  const creditsUsedColumn = {
    id: "creditsUsed",
    header: ({ column }: { column: Column<Participant, unknown> }) => (
      <DataTableColumnHeader column={column} title="Credits Used" />
    ),
    accessorKey: "creditsUsed",
    enableSorting: true,
    sortingFn: (rowA: Row<Participant>, rowB: Row<Participant>) => {
      const creditsA = +(rowA.original.creditsUsed || 0);
      const creditsB = +(rowB.original.creditsUsed || 0);
      return creditsA - creditsB;
    },
    cell: ({ row }: { row: Row<Participant> }) => {
      return (
        <Flex style={{ gap: 8 }}>
          <EmbedSvg src={CreditSVG} height={16} width={16} />
          {roundToOneDecimalPlace(row.original.creditsUsed || 0)}
        </Flex>
      );
    },
  };

  const nameColumn = {
    id: "name",
    header: ({ column }: { column: Column<Participant, unknown> }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    accessorKey: "user.name",
    enableSorting: true,
    sortingFn: (rowA: Row<Participant>, rowB: Row<Participant>) => {
      const nameA = rowA.original.user?.name?.toLowerCase() || "";
      const nameB = rowB.original.user?.name?.toLowerCase() || "";
      return nameA.localeCompare(nameB);
    },
    cell: ({ row }: { row: Row<Participant> }) => {
      return renderUserCell(row);
    },
  };

  const introColumns: ColumnDef<Participant>[] = [
    nameColumn,
    {
      id: "meetings",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Session" />
      ),
      accessorKey: "meetings",
      enableSorting: true,
      sortingFn: (rowA: Row<Participant>, rowB: Row<Participant>) => {
        const meetingsA = rowA.original.meetings;
        const meetingsB = rowB.original.meetings;
        const completedA =
          meetingsA?.filter(
            (meeting) =>
              meeting?.status &&
              [MeetingStatus.Complete, MeetingStatus.Ongoing].includes(
                meeting.status,
              ),
          ).length || 0;
        const completedB =
          meetingsB?.filter(
            (meeting) =>
              meeting?.status &&
              [MeetingStatus.Complete, MeetingStatus.Ongoing].includes(
                meeting.status,
              ),
          ).length || 0;
        return completedA - completedB;
      },
      cell: ({ row }: { row: Row<Participant> }) => {
        const meetings = row.original.meetings;
        const completedIntroCount = meetings?.filter(
          (meeting) =>
            meeting?.status &&
            [MeetingStatus.Complete, MeetingStatus.Ongoing].includes(
              meeting.status,
            ),
        ).length;

        const canceledIntroCount = meetings?.filter(
          (meeting) => meeting?.status === MeetingStatus.Canceled,
        ).length;

        const upcomingCount = meetings?.filter(
          (meeting) =>
            meeting?.status &&
            [
              MeetingStatus.Scheduling,
              MeetingStatus.Soon,
              MeetingStatus.Upcoming,
            ].includes(meeting.status),
        ).length;
        return (
          <>
            <Flex style={{ gap: 16 }}>
              {!!completedIntroCount && (
                <StatusBadge
                  icon={<OneOneSvg size={16} fillColor={DARK_GREEN} />}
                  status={"Completed"}
                  text={completedIntroCount || 0}
                  tooltip={`${pluralize({ amount: completedIntroCount, singular: "session", plural: "sessions" })} completed`}
                />
              )}
              {!!canceledIntroCount && (
                <StatusBadge
                  icon={<OneOneSvg size={16} fillColor={"#C61D1D"} />}
                  status={"Missed"}
                  text={canceledIntroCount || 0}
                  tooltip={`${pluralize({ amount: canceledIntroCount, singular: "session", plural: "sessions" })} canceled`}
                />
              )}
              {!!upcomingCount && (
                <StatusBadge
                  icon={<OneOneSvg size={16} fillColor={"#778397"} />}
                  status={"Upcoming"}
                  text={upcomingCount || 0}
                  tooltip={`${pluralize({ amount: upcomingCount, singular: "session", plural: "sessions" })} scheduled`}
                />
              )}
              {!completedIntroCount &&
                !canceledIntroCount &&
                !upcomingCount &&
                "-"}
            </Flex>
          </>
        );
      },
    },
    stageColumn,
    coachesColumn,
    creditsUsedColumn,
  ];

  const sessionColumns: ColumnDef<Participant>[] = [
    nameColumn,
    {
      id: "eventAt",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Meeting At" />
      ),
      accessorKey: "eventAt",
      enableSorting: true,
      sortingFn: (rowA: Row<Participant>, rowB: Row<Participant>) => {
        const meetingA = rowA.original.meetings?.[0];
        const meetingB = rowB.original.meetings?.[0];
        const dateA = new Date(meetingA?.eventAt || "");
        const dateB = new Date(meetingB?.eventAt || "");
        return dateA.getTime() - dateB.getTime();
      },
      cell: ({ row }: { row: Row<Participant> }) => {
        const meeting = row.original.meetings?.[0];

        return meeting?.eventAt
          ? formatDate(meeting.eventAt, "MMM d, hh:mm a")
          : "-";
      },
    },
    stageColumn,
    coachesColumn,
    creditsUsedColumn,
  ];

  const regularColumns: ColumnDef<Participant>[] = [
    nameColumn,
    {
      id: "status",
      header: ({ column }: { column: Column<Participant, unknown> }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      accessorKey: "status",
      enableSorting: true,
      sortingFn: (rowA: Row<Participant>, rowB: Row<Participant>) => {
        const statusA = rowA.original.participantCompletionStatus || "";
        const statusB = rowB.original.participantCompletionStatus || "";
        return statusA.localeCompare(statusB);
      },
      cell: ({ row }: { row: Row<Participant> }) => {
        return renderStatusCell(row, { isCloseEarly });
      },
    },
  ];

  if (component?.type === ComponentTypeEnum.OneOneCoachingIntro) {
    return { columns: introColumns };
  }

  if (component?.type === ComponentTypeEnum.OneOneCoachingSession) {
    return { columns: sessionColumns };
  }

  return { columns: regularColumns };
};

type Session = NonNullableArrayItem<
  GetProgramCoachingSessionsQuery["programCoachingSessions"]
>;

type Component = (MainComponent | Session) & {
  closedAt?: string | null;
};

interface ProgramParticipantsTableProps {
  loading?: boolean;
  participants: Participant[];
  component?: Component;
}

export function ParticipantResponseStatusTableV2({
  participants,
  component,
}: ProgramParticipantsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "name",
      desc: false,
    },
  ]);

  const isCloseEarly = component ? isCloseEarlyComponent(component) : false;

  const { columns } = useResponseStatusColumns({
    component,
    isCloseEarly: isCloseEarly,
  });

  const table = useReactTable({
    data: participants,
    columns,
    state: {
      sorting,
    },
    enableSorting: true,
    enableHiding: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row?.id || "",
  });

  return (
    <DataTable
      table={table}
      className="overflow-visible"
      totalRecords={participants.length}
      pagination={false}
      emptyState={
        <div className="flex flex-col items-center justify-center py-8">
          <div className="flex flex-col items-center justify-center text-gray-500">
            No participants
          </div>
        </div>
      }
    >
      <DataTableToolbar table={table}></DataTableToolbar>
    </DataTable>
  );
}
